import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOMAINS = ["Health", "Wealth", "Love", "Career", "Property", "Marriage"];

const EMPTY = { name: "", email: "", domain: "Health", birth_info: "", message: "" };

const inputCls =
  "w-full bg-ink/60 border border-gold/25 rounded-lg px-4 py-3.5 text-sm text-parchment placeholder:text-parchment-muted/50 focus:outline-none focus:border-gold/70 focus:ring-1 focus:ring-gold/40 transition-colors duration-300";

export const EnquiryForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        birth_info: form.birth_info || null,
      });
      toast.success("Your petition has been received. The practitioner will respond shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("The raven lost your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="enquiry"
      data-testid="enquiry-section"
      className="relative py-28 sm:py-36 bg-ink2 border-t border-gold/10"
    >
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <p className="text-xs uppercase tracking-[0.35em] text-gold/80 font-mono mb-4">
            Send Your Petition
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-parchment">
            Consultation & enquiry
          </h2>
          <p className="mt-5 text-sm sm:text-base text-parchment-muted leading-relaxed max-w-xl mx-auto">
            Describe your situation in confidence. Every petition is read by the
            practitioner and answered with an honest assessment of the working required.
          </p>
        </motion.div>

        <motion.form
          data-testid="enquiry-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="parchment-card rounded-2xl p-8 sm:p-12 space-y-6"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="enquiry-name" className="block text-[10px] uppercase tracking-[0.3em] font-mono text-gold/70 mb-2.5">
                Full Name
              </label>
              <input
                id="enquiry-name"
                data-testid="enquiry-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="enquiry-email" className="block text-[10px] uppercase tracking-[0.3em] font-mono text-gold/70 mb-2.5">
                Email Address
              </label>
              <input
                id="enquiry-email"
                data-testid="enquiry-email-input"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="you@example.com"
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="enquiry-domain" className="block text-[10px] uppercase tracking-[0.3em] font-mono text-gold/70 mb-2.5">
                Seeking Domain
              </label>
              <select
                id="enquiry-domain"
                data-testid="enquiry-domain-select"
                value={form.domain}
                onChange={set("domain")}
                className={inputCls}
              >
                {DOMAINS.map((d) => (
                  <option key={d} value={d} className="bg-ink">
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="enquiry-birth" className="block text-[10px] uppercase tracking-[0.3em] font-mono text-gold/70 mb-2.5">
                Birth Date & Time <span className="text-parchment-muted/60">(optional)</span>
              </label>
              <input
                id="enquiry-birth"
                data-testid="enquiry-birth-input"
                value={form.birth_info}
                onChange={set("birth_info")}
                placeholder="e.g. 14 Mar 1990, 06:45 AM"
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label htmlFor="enquiry-message" className="block text-[10px] uppercase tracking-[0.3em] font-mono text-gold/70 mb-2.5">
              Your Intention & Situation
            </label>
            <textarea
              id="enquiry-message"
              data-testid="enquiry-message-input"
              required
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder="Speak freely. All petitions are held in strict confidence…"
              className={`${inputCls} resize-none`}
            />
          </div>

          <button
            data-testid="enquiry-form-submit-button"
            type="submit"
            disabled={sending}
            className="w-full bg-gold text-ink rounded-full py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-amber transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {sending ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Sending…
              </>
            ) : (
              "Seal & Send Petition"
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
